import './App.css';
import './animations.css';
import './scrollAnimations.js';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import { LanguageProvider } from './context/LanguageContext';
import './utils/userValidator.js'; // Import user validator to auto-initialize

// Components
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import UserProtectedRoute from './components/UserProtectedRoute';
import StreamerRoute from './components/StreamerRoute';

// Pages
import Home from './pages/Home';
import Submit from './pages/Submit';
import Gallery from './pages/Gallery';
import TopVoters from './pages/TopVoters';
import TopCreators from './pages/TopCreators';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import ForgotPassword from './pages/ForgotPassword';
import AdminDashboard from './pages/AdminDashboard';
import AdminDashboardOwner from './pages/AdminDashboardOwner';
import AdminStore from './pages/AdminStore';
import AdminOrder from './pages/AdminOrder';
import Customer from './pages/Customer';
import UserDashboard from './pages/UserDashboard';
import ChallengesList from './pages/ChallengesList';
import TermsAndConditions from './pages/TermsAndConditions';
import LegalNotices from './pages/LegalNotices';
import Shop from './components/Shop';
import Application from './pages/Application';
import Settings from './pages/Settings';
import Streamer from './pages/Streamer';

function App() {
  // Initialize scroll animations
  useEffect(() => {
    // Check if the initScrollAnimations function is available
    if (typeof window.initScrollAnimations === 'function') {
      window.initScrollAnimations();
    }

    // Ensure elements are visible even if JS fails
    const animatedElements = document.querySelectorAll('.slide-in-left, .slide-in-right, .fade-in');
    animatedElements.forEach(el => el.classList.add('active'));
  }, []);
  
  return (
    <ErrorBoundary>
    <LanguageProvider>
      <AuthProvider>
        <AdminProvider>
          <Router>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="submit" element={<Submit />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="top-voters" element={<TopVoters />} />
              <Route path="top-creators" element={<TopCreators />} />
              <Route path="login" element={<Login />} />
              <Route path="shop" element={<Shop />} />
              <Route path="application" element={<Application />} />
              <Route path="streamer" element={<Streamer />} />
              <Route path="terms" element={<TermsAndConditions />} />
              <Route path="legal-notices" element={<LegalNotices />} />
            </Route>
            {/* Admin Routes (without Layout) */}
                    <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute requireOwner="admin">
                <AdminDashboardOwner />
              </ProtectedRoute>
            } />
            <Route path="/admin/store" element={
              <ProtectedRoute requireOwner="admin">
                <AdminStore />
              </ProtectedRoute>
            } />
            <Route path="/admin/orders" element={
              <ProtectedRoute requireOwner="admin">
                <AdminOrder />
              </ProtectedRoute>
            } />
            <Route path="/admin/payments" element={<Navigate to="/admin/orders" replace />} />
            <Route path="/admin/customers" element={
              <ProtectedRoute requireOwner="admin">
                <Customer />
              </ProtectedRoute>
            } />
        <Route path="/streamer/dashboard" element={
          <ProtectedRoute requireOwner="not-admin">
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/streamer/challenges-list" element={
          <ProtectedRoute requireOwner="not-admin">
            <ChallengesList />
          </ProtectedRoute>
        } />
        <Route path="/streamer/settings" element={
          <ProtectedRoute requireOwner="not-admin">
            <Settings />
          </ProtectedRoute>
        } />
            {/* User Routes (without Layout) */}
            <Route path="/user/" element={<Navigate to="/" replace />} />
            <Route path="/user/dashboard" element={
              <UserProtectedRoute>
                <UserDashboard />
              </UserProtectedRoute>
            } />
            
            {/* Dynamic Streamer Routes - MUST BE LAST to avoid conflicts */}
            <Route path="/:streamer" element={<StreamerRoute />} />
            <Route path="/:streamer/:page" element={<StreamerRoute />} />
          </Routes>
          </Router>
        </AdminProvider>
      </AuthProvider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
